import {
  CheckOutlined,
  DeleteOutlined,
  EyeOutlined,
  KeyOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { http } from '../../api/client.js';
import { tx } from '../../i18n/translate.js';
import { AdminUserDetailDrawer } from './AdminUserDetailDrawer.js';
import { CreateUserModal } from './CreateUserModal.js';
import { PLATFORM_ROLE_TYPES_PARAM, isPlatformRoleType } from './platform-permission-types.js';

interface UserRow {
  systemuserid: string;
  fullname: string | null;
  domainname: string | null;
  internalemailaddress: string | null;
  employee_number: string | null;
  avatar_url: string | null;
  jobtitle: string | null;
  mobilephone: string | null;
  workplace: string | null;
  isdisabled: boolean | null;
  businessunit: { name: string | null } | null;
  businessunits?: Array<{
    businessunitid: string;
    name: string | null;
    dingtalkDepartmentId: string | null;
    dingtalkSortOrder: number | null;
    source: string;
  }>;
  roles: Array<{ roleid: string; name: string; roleType: string }>;
  createdon: string | null;
  modifiedon: string | null;
}

interface RoleRow {
  roleid: string;
  name: string;
  roleType: string;
  businessunit?: { name: string | null } | null;
}

interface AccessRoleRow {
  role_catalog_id: string;
  name: string;
  role_type: string;
  status: string;
  metadata?: {
    hasGlobalAccess?: boolean;
    requiresApproval?: boolean;
    riskLevel?: string;
  };
}

interface AccessAssignmentRow {
  access_assignment_id: string;
  source_type: string;
  state: string;
  role?: AccessRoleRow;
}

interface BuNode {
  key: string;
  title: string;
  children: BuNode[];
}

interface BuOption {
  businessunitid: string;
  name: string;
}

const PLATFORM_ROLE_TYPES = PLATFORM_ROLE_TYPES_PARAM;

export function AdminUsersPage() {
  const { message, modal } = App.useApp();
  const [rows, setRows] = useState<UserRow[]>([]);
  const [stats, setStats] = useState<{
    users: number;
    active: number;
    disabled: number;
    roles: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [permissionUser, setPermissionUser] = useState<UserRow | null>(null);
  const [buFilter, setBuFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [buOptions, setBuOptions] = useState<BuOption[]>([]);
  const [roleOptions, setRoleOptions] = useState<RoleRow[]>([]);

  const refresh = useCallback(() => {
    setLoading(true);
    const params: Record<string, string | number> = { q, page, pageSize: 30 };
    if (buFilter !== 'all') params.buId = buFilter;
    if (roleFilter !== 'all') params.roleId = roleFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    Promise.all([
      http.get<{ rows: UserRow[]; total: number }>('/admin/users', {
        params,
      }),
      http.get<{ users: number; active: number; disabled: number; roles: number }>(
        '/admin/users/stats',
      ),
    ])
      .then(([r1, r2]) => {
        setRows(r1.data.rows);
        setTotal(r1.data.total);
        setStats(r2.data);
      })
      .finally(() => setLoading(false));
  }, [q, page, buFilter, roleFilter, statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    Promise.all([
      http.get<{ nodes: BuNode[] }>('/admin/users/bu-tree'),
      http.get<{ rows: RoleRow[] }>('/admin/users/roles', {
        params: { q: '', roleTypes: PLATFORM_ROLE_TYPES },
      }),
    ])
      .then(([buRes, roleRes]) => {
        const flat: BuOption[] = [];
        const walk = (nodes: BuNode[]) => {
          for (const n of nodes) {
            flat.push({ businessunitid: n.key, name: n.title });
            walk(n.children ?? []);
          }
        };
        walk(buRes.data.nodes);
        setBuOptions(flat);
        setRoleOptions(roleRes.data.rows);
      })
      .catch(() => {
        setBuOptions([]);
        setRoleOptions([]);
      });
  }, []);

  const resetPassword = (u: UserRow) => {
    let next = 'Crest@2026';
    modal.confirm({
      title: tx('hardcoded:pages.admin.adminuserspage.resetPasswordTitle', {
        v0: u.fullname ?? u.domainname ?? '',
      }),
      content: (
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Typography.Text type="secondary">
            {tx('hardcoded:pages.admin.adminuserspage.resetPasswordDescription')}
          </Typography.Text>
          <Input.Password
            defaultValue={next}
            placeholder={tx('hardcoded:pages.admin.adminuserspage.resetPasswordPlaceholder')}
            onChange={(e) => {
              next = e.target.value;
            }}
            autoFocus
          />
        </Space>
      ),
      okText: tx('hardcoded:pages.admin.adminuserspage.reset'),
      cancelText: tx('common:cancel'),
      onOk: async () => {
        try {
          const newPassword = (next ?? '').trim() || 'Crest@2026';
          await http.post(`/admin/users/${u.systemuserid}/reset-password`, { newPassword });
          message.success(
            tx('hardcoded:pages.admin.adminuserspage.resetPasswordSuccess', {
              v0: u.fullname ?? u.domainname,
              v1: newPassword,
            }),
          );
        } catch (e: unknown) {
          const err = e as { response?: { data?: { message?: string } } };
          message.error(
            err.response?.data?.message ??
              tx('hardcoded:pages.admin.adminuserspage.resetPasswordFailed'),
          );
        }
      },
    });
  };

  const removeUser = (u: UserRow) => {
    const displayName = u.fullname ?? u.domainname ?? u.systemuserid;
    modal.confirm({
      title: tx('hardcoded:pages.admin.adminuserspage.delete.title', { v0: displayName }),
      content: (
        <Space direction="vertical" size={4}>
          <Typography.Text>
            {tx('hardcoded:pages.admin.adminuserspage.delete.body')}
          </Typography.Text>
          <Typography.Text type="warning" style={{ fontSize: 12 }}>
            {tx('hardcoded:pages.admin.adminuserspage.delete.warn')}
          </Typography.Text>
        </Space>
      ),
      okText: tx('hardcoded:pages.admin.adminuserspage.delete.ok'),
      cancelText: tx('legacy:pages.admin.adminuserspage.0b131f60'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const r = await http.delete<{ message?: string }>(`/admin/users/${u.systemuserid}`);
          message.success(
            r.data?.message ?? tx('hardcoded:pages.admin.adminuserspage.delete.success'),
          );
          refresh();
        } catch (e: unknown) {
          const err = e as { response?: { data?: { message?: string } } };
          message.error(
            err.response?.data?.message ?? tx('hardcoded:pages.admin.adminuserspage.delete.fail'),
          );
        }
      },
    });
  };

  const setDisabled = async (u: UserRow, disabled: boolean) => {
    modal.confirm({
      title: disabled
        ? tx('legacy:pages.admin.adminuserspage.a9d032b9')
        : tx('legacy:pages.admin.adminuserspage.0cb05934'),
      content: tx('hardcoded:pages.admin.adminuserspage.8c99ec9f', {
        v0: u.fullname ?? u.domainname,
        v1: disabled
          ? tx('hardcoded:pages.admin.adminuserspage.90a80c76')
          : tx('hardcoded:pages.admin.adminuserspage.da4d04c1'),
      }),
      okText: tx('legacy:pages.admin.adminuserspage.17cfbbd0'),
      cancelText: tx('legacy:pages.admin.adminuserspage.0b131f60'),
      okButtonProps: { danger: disabled },
      onOk: async () => {
        try {
          await http.post(`/admin/users/${u.systemuserid}/${disabled ? 'disable' : 'enable'}`);
          message.success(
            tx('hardcoded:pages.admin.adminuserspage.3ab20da5', {
              v0: disabled
                ? tx('hardcoded:pages.admin.adminuserspage.12a61341')
                : tx('hardcoded:pages.admin.adminuserspage.5e543ce4'),
            }),
          );
          refresh();
        } catch (e: unknown) {
          const err = e as { response?: { data?: { message?: string } } };
          message.error(
            err.response?.data?.message ?? tx('hardcoded:pages.admin.adminuserspage.cc6bb5c7'),
          );
        }
      },
    });
  };

  // 部分 D365 export 的 domainname 含 32 位 hex 前缀 (UUID 无连字符) — 显示时剥离
  const cleanDomain = (d: string | null | undefined): string | null => {
    if (!d) return null;
    return d.replace(/^[0-9a-f]{32}/i, '');
  };
  // fullname 也可能残留 # 前缀/后缀 (虽 migration 已洗一次, 防御性兜底)
  const cleanName = (n: string | null | undefined): string => {
    if (!n) return tx('hardcoded:pages.admin.adminuserspage.246e580d');
    return (
      n.replace(/^#\s*/, '').replace(/\s*#$/, '').trim() ||
      tx('hardcoded:pages.admin.adminuserspage.246e580d')
    );
  };

  const columns: ColumnsType<UserRow> = [
    {
      title: tx('legacy:pages.admin.adminuserspage.2f0008a4'),
      dataIndex: 'fullname',
      render: (v: string | null, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{cleanName(v)}</Typography.Text>
          {row.domainname && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {cleanDomain(row.domainname)}
            </Typography.Text>
          )}
          {row.jobtitle && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {row.jobtitle}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: '工号',
      dataIndex: 'employee_number',
      width: 130,
      render: (v: string | null) => v || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: tx('legacy:pages.admin.adminuserspage.b348315f'),
      dataIndex: 'internalemailaddress',
      width: 240,
      render: (v: string | null, row) => (
        <Space direction="vertical" size={0}>
          {v ? (
            <Typography.Text copyable style={{ fontSize: 12 }}>
              {v}
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          )}
          {row.mobilephone && (
            <Typography.Text copyable type="secondary" style={{ fontSize: 11 }}>
              {row.mobilephone}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: tx('legacy:pages.admin.adminuserspage.d0cf70a8'),
      dataIndex: 'businessunit',
      width: 260,
      render: (_: { name: string | null } | null, row) => {
        const businessunits =
          row.businessunits && row.businessunits.length > 0
            ? row.businessunits
            : row.businessunit?.name
              ? [
                  {
                    businessunitid: row.businessunit.name,
                    name: row.businessunit.name,
                    dingtalkDepartmentId: null,
                    dingtalkSortOrder: null,
                    source: 'legacy',
                  },
                ]
              : [];
        return businessunits.length > 0 ? (
          <Space direction="vertical" size={2}>
            <Space size={[4, 4]} wrap>
              {businessunits.slice(0, 4).map((bu) => (
                <Tag key={bu.businessunitid} color="cyan" style={{ marginInlineEnd: 0 }}>
                  {bu.name ?? bu.businessunitid}
                </Tag>
              ))}
              {businessunits.length > 4 && <Tag>+{businessunits.length - 4}</Tag>}
            </Space>
            {row.workplace && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {row.workplace}
              </Typography.Text>
            )}
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        );
      },
    },
    {
      title: tx('legacy:pages.admin.adminuserspage.07f977b4'),
      dataIndex: 'roles',
      render: (roles: UserRow['roles']) => {
        const platformRoles = roles.filter((r) => isPlatformRoleType(r.roleType));
        return platformRoles.length === 0 ? (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {tx('hardcoded:pages.admin.adminuserspage.255237ca')}
          </Typography.Text>
        ) : (
          <Space size={4} wrap>
            {platformRoles.slice(0, 3).map((r) => (
              <Tag key={r.roleid} color="blue">
                {r.name}
              </Tag>
            ))}
            {platformRoles.length > 3 && <Tag>+{platformRoles.length - 3}</Tag>}
          </Space>
        );
      },
    },
    {
      title: tx('hardcoded:pages.admin.adminuserspage.7aef16ae'),
      dataIndex: 'isdisabled',
      width: 100,
      render: (v: boolean | null) =>
        v ? (
          <Tag color="default">{tx('legacy:pages.admin.adminuserspage.85c5cb55')}</Tag>
        ) : (
          <Tag color="green">{tx('legacy:pages.admin.adminuserspage.1697a1bc')}</Tag>
        ),
    },
    {
      title: tx('hardcoded:pages.admin.adminuserspage.01112768'),
      width: 280,
      render: (_, row) => (
        <Space size={4} wrap>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setDetailUserId(row.systemuserid)}
          >
            详情
          </Button>
          <Button
            size="small"
            icon={<UserSwitchOutlined />}
            onClick={() => setPermissionUser(row)}
          >
            {tx('accessCenter:configureUserRoles')}
          </Button>
          <Button size="small" icon={<KeyOutlined />} onClick={() => resetPassword(row)}>
            重置密码
          </Button>
          {row.isdisabled ? (
            <Button size="small" icon={<CheckOutlined />} onClick={() => setDisabled(row, false)}>
              {tx('hardcoded:pages.admin.adminuserspage.5e543ce4')}
            </Button>
          ) : (
            <Button
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => setDisabled(row, true)}
            >
              {tx('hardcoded:pages.admin.adminuserspage.12a61341')}
            </Button>
          )}
          <Button
            size="small"
            danger
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => removeUser(row)}
            title={tx('hardcoded:pages.admin.adminuserspage.delete.btn')}
          >
            {tx('hardcoded:pages.admin.adminuserspage.delete.btn')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {tx('legacy:pages.admin.adminuserspage.42e5b836')}
            </Typography.Title>
            <Tag color="blue">
              {tx('hardcoded:pages.admin.adminuserspage.0ea0db04')}
              {total} {tx('hardcoded:pages.admin.adminuserspage.06b6ea06')}
            </Tag>
          </Space>
        }
        extra={
          <Space>
            <Button type="primary" icon={<UserAddOutlined />} onClick={() => setCreateOpen(true)}>
              {tx('hardcoded:pages.admin.adminuserspage.f6651e17')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={refresh}>
              {tx('hardcoded:pages.admin.adminuserspage.19687226')}
            </Button>
          </Space>
        }
      >
        {stats && (
          <Row gutter={[12, 12]}>
            <Col xs={12} md={6}>
              <Card size="small" bodyStyle={{ padding: '8px 12px' }}>
                <Statistic
                  title={
                    <Space size={4}>
                      <TeamOutlined style={{ color: '#1677ff' }} />
                      <span>{tx('legacy:pages.admin.adminuserspage.750d8a77')}</span>
                    </Space>
                  }
                  value={stats.users}
                  valueStyle={{ fontSize: 22 }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" bodyStyle={{ padding: '8px 12px' }}>
                <Statistic
                  title={
                    <Space size={4}>
                      <CheckOutlined style={{ color: '#52c41a' }} />
                      <span>{tx('legacy:pages.admin.adminuserspage.1697a1bc')}</span>
                    </Space>
                  }
                  value={stats.active}
                  valueStyle={{ fontSize: 22, color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" bodyStyle={{ padding: '8px 12px' }}>
                <Statistic
                  title={
                    <Space size={4}>
                      <StopOutlined style={{ color: '#8c8c8c' }} />
                      <span>{tx('legacy:pages.admin.adminuserspage.85c5cb55')}</span>
                    </Space>
                  }
                  value={stats.disabled}
                  valueStyle={{ fontSize: 22, color: '#8c8c8c' }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" bodyStyle={{ padding: '8px 12px' }}>
                <Statistic
                  title={
                    <Space size={4}>
                      <UserSwitchOutlined style={{ color: '#722ed1' }} />
                      <span>{tx('legacy:pages.admin.adminuserspage.07f977b4')}</span>
                    </Space>
                  }
                  value={stats.roles}
                  valueStyle={{ fontSize: 22, color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>
        )}
      </Card>
      <Card size="small">
        <Space style={{ marginBottom: 12 }}>
          <Input
            allowClear
            placeholder={tx('hardcoded:pages.admin.adminuserspage.2e477ecb')}
            prefix={<SearchOutlined />}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            style={{ width: 320 }}
          />
          <Select
            showSearch
            value={buFilter}
            style={{ width: 180 }}
            optionFilterProp="label"
            onChange={(value) => {
              setBuFilter(value);
              setPage(1);
            }}
            options={[
              { value: 'all', label: '全部企业组织' },
              ...buOptions.map((bu) => ({ value: bu.businessunitid, label: bu.name })),
            ]}
          />
          <Select
            showSearch
            value={roleFilter}
            style={{ width: 180 }}
            optionFilterProp="label"
            onChange={(value) => {
              setRoleFilter(value);
              setPage(1);
            }}
            options={[
              { value: 'all', label: '全部平台角色' },
              ...roleOptions.map((role) => ({ value: role.roleid, label: role.name })),
            ]}
          />
          <Select
            value={statusFilter}
            style={{ width: 130 }}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
            options={[
              { value: 'all', label: '全部状态' },
              { value: 'active', label: '启用' },
              { value: 'disabled', label: '禁用' },
            ]}
          />
        </Space>
        <Table
          rowKey="systemuserid"
          columns={columns}
          dataSource={rows}
          size="middle"
          pagination={{
            current: page,
            pageSize: 30,
            total,
            showTotal: (t) => tx('hardcoded:pages.admin.adminuserspage.1f9ac7d0', { v0: t }),
            onChange: setPage,
          }}
          scroll={{ x: 'max-content' }}
          sticky
        />
      </Card>

      <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={refresh} />
      <AdminUserDetailDrawer userId={detailUserId} onClose={() => setDetailUserId(null)} />
      <UserPermissionModal
        user={permissionUser}
        onClose={() => setPermissionUser(null)}
        onSaved={() => {
          setPermissionUser(null);
          refresh();
        }}
      />
    </Spin>
  );
}

const EFFECTIVE_ASSIGNMENT_STATES = new Set([
  'pending',
  'applying',
  'active',
  'failed',
  'retrying',
  'approval_pending',
  'approval_failed',
]);

function isDirectGrantableRole(role: AccessRoleRow) {
  return (
    !['system', 'admin'].includes(role.role_type) &&
    role.metadata?.requiresApproval !== true &&
    role.metadata?.hasGlobalAccess !== true &&
    role.metadata?.riskLevel !== 'high'
  );
}

export function UserPermissionModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { message } = App.useApp();
  const [roles, setRoles] = useState<AccessRoleRow[]>([]);
  const [assignments, setAssignments] = useState<AccessAssignmentRow[]>([]);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    let active = true;
    setLoading(true);
    Promise.all([
      http.get<{ rows: AccessRoleRow[] }>('/admin/access/roles', {
        params: { domain: 'crest', status: 'active', pageSize: 200 },
      }),
      http.get<{ rows: AccessAssignmentRow[] }>('/admin/access/assignments', {
        params: { domain: 'crest', userId: user.systemuserid, pageSize: 200 },
      }),
    ])
      .then(([roleResponse, assignmentResponse]) => {
        if (!active) return;
        const nextAssignments = (assignmentResponse.data.rows ?? []).filter((assignment) =>
          EFFECTIVE_ASSIGNMENT_STATES.has(assignment.state),
        );
        setRoles(roleResponse.data.rows ?? []);
        setAssignments(nextAssignments);
        setRoleIds(
          [...new Set(nextAssignments.flatMap((assignment) => assignment.role?.role_catalog_id ?? []))],
        );
      })
      .catch(() => {
        if (!active) return;
        setRoles([]);
        setAssignments([]);
        setRoleIds([]);
        message.error('权限配置加载失败，请重试');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [message, user]);

  const inheritedRoleIds = new Set(
    assignments
      .filter((assignment) => assignment.source_type !== 'direct')
      .flatMap((assignment) => assignment.role?.role_catalog_id ?? []),
  );

  const save = async () => {
    if (!user) return;
    const currentRoleIds = new Set(
      assignments.flatMap((assignment) => assignment.role?.role_catalog_id ?? []),
    );
    const nextRoleIds = new Set(roleIds);
    const addedRoleIds = roleIds.filter((roleId) => !currentRoleIds.has(roleId));
    const removedAssignments = assignments.filter(
      (assignment) =>
        assignment.source_type === 'direct' &&
        assignment.role &&
        !nextRoleIds.has(assignment.role.role_catalog_id),
    );

    setSaving(true);
    try {
      for (const roleCatalogId of addedRoleIds) {
        await http.post('/admin/access/assignments/direct', {
          userId: user.systemuserid,
          roleCatalogId,
          scopePolicy: 'role_default',
          scopeConfig: {},
        });
      }
      for (const assignment of removedAssignments) {
        await http.post(`/admin/access/assignments/${assignment.access_assignment_id}/revoke`);
      }
      message.success('用户平台角色已更新');
      onSaved();
    } catch (error) {
      const detail = (error as { response?: { data?: { message?: string } } }).response?.data
        ?.message;
      message.error(detail ?? '用户平台角色更新失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={Boolean(user)}
      title={`权限配置 · ${cleanUserName(user)}`}
      okText="保存"
      cancelText="取消"
      confirmLoading={saving}
      onOk={save}
      onCancel={onClose}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">
        选择该用户可直接授予的平台角色。岗位继承或审批授权的角色会保留，需在对应授权来源中调整。
      </Typography.Paragraph>
      <Typography.Text strong>可选平台角色</Typography.Text>
      <Select
        aria-label="可选平台角色"
        mode="multiple"
        allowClear
        loading={loading}
        disabled={loading}
        value={roleIds}
        onChange={(nextRoleIds) =>
          setRoleIds([...new Set([...nextRoleIds, ...inheritedRoleIds])])
        }
        style={{ width: '100%', marginTop: 8 }}
        placeholder="选择一个或多个平台角色"
        options={roles
          .filter((role) => isDirectGrantableRole(role) || roleIds.includes(role.role_catalog_id))
          .map((role) => ({
            value: role.role_catalog_id,
            label: role.name,
            disabled: inheritedRoleIds.has(role.role_catalog_id),
          }))}
      />
    </Modal>
  );
}

function cleanUserName(user: UserRow | null) {
  if (!user) return '';
  return (
    user.fullname?.replace(/^#\s*/, '').replace(/\s*#$/, '').trim() || user.domainname || '用户'
  );
}
